import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

// third-party
import { FormattedMessage } from 'react-intl';

// project imports
import MainCard from '@components/ui/cards/MainCard';
import Transitions from '@components/extended/Transitions';
import useConfig from '@hooks/useConfig';

// assets
import { IconLogout } from '@tabler/icons-react';

// types
import SidebarUserAvatar from '@layout/MainLayout/ProfileSection/SidebarUserAvatar';
import HeaderUserAvatar from '@layout/MainLayout/ProfileSection/HeaderUserAvatar';
import LogoutModal from '@layout/MainLayout/ProfileSection/LogoutModal';
import { LOGIN_PATH } from '@app/config';
import { useAuthSelector } from '@providers/AuthProvider/ui/AuthProvider';

type ProfileSectionProps = {
  fromSidebar: boolean;
};

function ProfileSection({ fromSidebar }: ProfileSectionProps) {
  const theme = useTheme();
  const { borderRadius } = useConfig();
  const navigate = useNavigate();

  const [selectedIndex] = useState(-1);
  const logout = useAuthSelector((state) => state.logout);
  const userData = useAuthSelector((state) => state.userData);
  const [open, setOpen] = useState(false);
  const [logoutModalOpened, setLogoutModalOpened] = useState(false);

  /**
   * anchorRef is used on different components and specifying one type leads to other components throwing an error
   * */
  const anchorRef = useRef<any>(null);
  const handleLogout = async () => {
    try {
      await logout();
      navigate(LOGIN_PATH);
    } catch (err) {
      console.error(err);
    }
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (
    event: React.MouseEvent<HTMLDivElement> | MouseEvent | TouchEvent
  ) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }

    setOpen(false);
  };

  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current && !open) {
      anchorRef.current.focus();
    }

    prevOpen.current = open;
  }, [open]);

  const openLogoutModal = () => {
    setOpen(false);
    setLogoutModalOpened(true);
  };

  return (
    <>
      {fromSidebar ? (
        <SidebarUserAvatar
          open={open}
          handleToggle={handleToggle}
          handleLogout={openLogoutModal}
          anchorRef={anchorRef}
        />
      ) : (
        <HeaderUserAvatar
          open={open}
          handleToggle={handleToggle}
          anchorRef={anchorRef}
        />
      )}

      <Popper
        placement={fromSidebar ? 'top' : 'bottom'}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        style={{ zIndex: 10000 }}
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 14],
            },
          },
        ]}
      >
        {({ TransitionProps }) => (
          <ClickAwayListener onClickAway={handleClose}>
            <Transitions in={open} {...TransitionProps}>
              <Paper>
                {open && (
                  <MainCard
                    border={false}
                    elevation={16}
                    content={false}
                    boxShadow
                    borderRadius="10px"
                    shadow={theme.shadows[16]}
                  >
                    <Box sx={{ p: 2 }}>
                      <Stack>
                        <Stack
                          direction="row"
                          spacing={0.5}
                          alignItems="center"
                        >
                          <Typography variant="h4">Hello,</Typography>
                          <Typography
                            component="span"
                            variant="h4"
                            sx={{ fontWeight: 400 }}
                          >
                            {userData?.name}
                          </Typography>
                        </Stack>
                        <Typography variant="subtitle2">
                          {userData?.email}
                        </Typography>
                      </Stack>
                    </Box>
                    <Box sx={{ p: 2, pt: 0 }}>
                      <Divider />
                      <List
                        component="nav"
                        sx={{
                          width: '100%',
                          maxWidth: 350,
                          minWidth: 300,
                          borderRadius: `${borderRadius}px`,
                          '& .MuiListItemButton-root': { mt: 0.5 },
                        }}
                      >
                        <ListItemButton
                          sx={{ borderRadius: `${borderRadius}px` }}
                          selected={selectedIndex === 4}
                          onClick={openLogoutModal}
                        >
                          <ListItemIcon>
                            <IconLogout stroke={1.5} size="20px" />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography variant="body2">
                                <FormattedMessage id="logout" />
                              </Typography>
                            }
                          />
                        </ListItemButton>
                      </List>
                    </Box>
                  </MainCard>
                )}
              </Paper>
            </Transitions>
          </ClickAwayListener>
        )}
      </Popper>
      {logoutModalOpened && (
        <LogoutModal
          handleClose={() => setLogoutModalOpened(false)}
          handleConfirm={handleLogout}
          showModal={logoutModalOpened}
        />
      )}
    </>
  );
}

export default ProfileSection;
